import { createContext, useState, useCallback, ReactNode } from "react";
import api from "../../api/api";
import { ApiResponse, PaginationInfo } from "../../types/api";
import { showMessage } from "../FeedbackContext";

export const DailyTasksContext = createContext<any>(undefined);

interface DailyTasksProviderProps {
  children: ReactNode;
}

const toTaskApiPayload = (task: any) => ({
  assignedTo:
    task.assignedTo ||
    task.assigned_to ||
    task.assignedToId ||
    task.assigned_to_id ||
    "",
  work: task.work || "",
  taskTitle: task.taskTitle || task.task_title || task.taskName || "",
  taskDueDate:
    task.taskDueDate ||
    task.task_due_date ||
    task.dueDate ||
    task.end ||
    "",
  status: task.status || "Pending",
  description: task.description || "",
  activities: task.activities || task.remarks || "",
  projectId: task.projectId || task.project_id || null,
  createdDate:
    task.createdDate ||
    task.created_date ||
    task.start ||
    new Date().toISOString().split("T")[0],
});

const DailyTasksProvider = ({ children }: DailyTasksProviderProps) => {
  const [tasks, setTasks] = useState<any[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch tasks from the API with pagination
  const fetchTasks = useCallback(async (
    projectId?: number | null,
    page: number = 1,
    perPage: number = 15
  ) => {
    setLoading(true);
    setError(null);
    try {
      let url = '/daily-tasks';
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });
      if (projectId) {
        params.append('project_id', projectId.toString());
      }
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await api.get<any>(url);
      
      // Handle paginated response structure: { success: true, data: { data: [...], pagination: {...} } }
      let tasksData: any[] = [];
      let paginationData: PaginationInfo | null = null;
      const responseData = response.data;
      
      if (responseData?.success) {
        if (responseData.data?.data && Array.isArray(responseData.data.data)) {
          tasksData = responseData.data.data;
          paginationData = responseData.data.pagination || responseData.pagination;
        } else if (responseData.data && Array.isArray(responseData.data)) {
          tasksData = responseData.data;
          paginationData = responseData.pagination;
        } else if (Array.isArray(responseData)) {
          tasksData = responseData;
        }
      } else if (Array.isArray(responseData?.data)) {
        tasksData = responseData.data;
      } else if (Array.isArray(responseData)) {
        tasksData = responseData;
      }
      
      setTasks(tasksData);
      setPagination(paginationData);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to fetch tasks");
      console.error("Error fetching tasks:", err);
      setTasks([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Add a new task
  const addTask = useCallback(async (task: any) => {
    setLoading(true);
    setError(null);
    try {
      const payload = toTaskApiPayload(task);
      
      const response = await api.post<ApiResponse<any>>("/daily-tasks", payload);
      const newTask = response.data?.data || response.data;
      
      // Refresh tasks list after adding
      if (task.projectId || task.project_id) {
        await fetchTasks(task.projectId || task.project_id, 1, 15);
      } else {
        setTasks((prevTasks) => [...prevTasks, newTask]);
      }
      
      return newTask;
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message || "Failed to create task";
      setError(errorMsg);
      console.error("Error adding task:", err);
      throw err.response?.data || err;
    } finally {
      setLoading(false);
    }
  }, [fetchTasks]);

  // Edit an existing task
  const editTask = useCallback(async (updatedTask: any) => {
    setLoading(true);
    setError(null);
    try {
      const taskId = updatedTask.id;
      if (!taskId) {
        throw new Error("Task ID is required for update");
      }
      
      const payload = toTaskApiPayload(updatedTask);
      
      const response = await api.put<ApiResponse<any>>(`/daily-tasks/${taskId}`, payload);
      const updatedTaskData = response.data?.data || response.data;
      
      // Update local state
      setTasks((prevTasks) =>
        prevTasks.map((task) =>
          task.id === taskId ? updatedTaskData : task
        )
      );
      
      return updatedTaskData;
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message || "Failed to update task";
      setError(errorMsg);
      console.error("Error editing task:", err);
      throw err.response?.data || err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Delete a task
  const deleteTask = useCallback(async (id: number | string) => {
    setLoading(true);
    setError(null);
    try {
      const taskId = typeof id === 'string' ? parseInt(id, 10) : id;
      if (!taskId || isNaN(taskId)) {
        throw new Error("Invalid task ID");
      }
      
      await api.delete(`/daily-tasks/${taskId}`);
      
      // Update local state
      setTasks((prevTasks) => prevTasks.filter((task) => {
        const taskIdNum = typeof task.id === 'string' ? parseInt(task.id, 10) : task.id;
        return taskIdNum !== taskId;
      }));
      
      // If we have the project ID, refresh the list
      const deletedTask = tasks.find((t: any) => {
        const tId = typeof t.id === 'string' ? parseInt(t.id, 10) : t.id;
        return tId === taskId;
      });
      
      if (deletedTask?.project_id) {
        await fetchTasks(deletedTask.project_id, 1, 15);
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message || "Failed to delete task";
      setError(errorMsg);
      console.error("Error deleting task:", err);
      
      // Show user-friendly error message
      if (err.response?.status === 422) {
        showMessage(errorMsg);
      }
      
      throw err.response?.data || err;
    } finally {
      setLoading(false);
    }
  }, [tasks, fetchTasks]);

  // REMOVED auto-fetch useEffect - components will call fetchTasks explicitly

  return (
    <DailyTasksContext.Provider value={{ 
      tasks,
      pagination,
      loading, 
      error,
      fetchTasks, 
      addTask, 
      editTask, 
      deleteTask 
    }}>
      {children}
    </DailyTasksContext.Provider>
  );
};

export default DailyTasksProvider;
